import { conversionFor } from '@core/constants/role-layout.constant';
import { slotSeriesValue } from '@core/helpers/slot-series.helper';
import { Point } from '@core/models/installation.model';
import {
  PHASE_POWER_ROLES,
  phaseSum,
} from '@custom-components/sections/installation-insights/card-extras';
import {
  formatDisplayValue,
  sourceKey,
} from '@custom-components/sections/installation-insights/installation-card-set';
import { PvForecastSeries } from '@custom-components/sections/installation-insights/pv-forecast';

export interface ExplorerColumn {
  field: string;
  // The full, unambiguous header, as the download carries it.
  label: string;
  // The table's header: the name on one line, the qualifier and unit under it.
  name: string;
  detail: string;
  values: string[];
}

export interface ExplorerLabels {
  meterReading: (name: string) => string;
  phaseTotal: string;
  forecastWeather: string;
  forecastClearSky: string;
}

export interface ExplorerForecast {
  // The PV point the forecast is read for; its columns follow that point's column.
  pointId: string;
  series: PvForecastSeries;
}

export function withUnit(label: string, unit: string): string {
  const { displayUnit } = conversionFor(unit);
  return displayUnit ? `${label} (${displayUnit})` : label;
}

// A measured counter's value is the register reading at the slot's close. A derived counter's
// value is its quantity again, so it has no reading to show.
function isRegister(point: Point): boolean {
  return point.kind === 'counter' && point.nature === 'measured';
}

function currentSource(point: Point) {
  return point.sources.at(-1)?.source;
}

// The meter a point reads is named in its own name ("Voltage L1 (ABB 23)"). It moves to the
// header's second line, but only when the name ends with exactly the source's label, so
// parentheses that are part of a name nobody chose to split stay where they are.
function splitQualifier(point: Point): { name: string; qualifier: string | null } {
  const label = currentSource(point)?.label;
  const suffix = label ? ` (${label})` : null;
  if (label && suffix && point.name.endsWith(suffix) && point.name.length > suffix.length) {
    return { name: point.name.slice(0, -suffix.length), qualifier: label };
  }
  return { name: point.name, qualifier: null };
}

function detailLine(qualifier: string | null, unit: string): string {
  return [qualifier, conversionFor(unit).displayUnit].filter(part => !!part).join(' · ');
}

function phaseGroups(available: Point[]): Map<string, Point[]> {
  const groups = new Map<string, Point[]>();
  for (const point of available) {
    if (point.state === 'active' && PHASE_POWER_ROLES.has(point.role)) {
      const key = sourceKey(currentSource(point));
      groups.set(key, [...(groups.get(key) ?? []), point]);
    }
  }
  return new Map([...groups].filter(([, members]) => members.length > 1));
}

function lastSelectedPhases(selected: Point[], groups: Map<string, Point[]>): Map<string, string> {
  const lastSelectedPhase = new Map<string, string>();
  for (const point of selected) {
    const key = sourceKey(currentSource(point));
    if (PHASE_POWER_ROLES.has(point.role) && groups.has(key)) {
      lastSelectedPhase.set(key, point.pointId);
    }
  }
  return lastSelectedPhase;
}

// One column per selected point, each followed by what is read beside it: a counter's register
// reading, the total after a meter's last phase, and the PV forecast after the PV point. The total
// adds up every phase of the meter, not only the selected ones, since it is the meter's power.
export function explorerColumns(
  selected: Point[],
  available: Point[],
  slotCount: number,
  labels: ExplorerLabels,
  forecast: ExplorerForecast | null,
): ExplorerColumn[] {
  const groups = phaseGroups(available);
  const lastSelectedPhase = lastSelectedPhases(selected, groups);

  const slotValues = (point: Point, pick: (index: number) => number | null): string[] =>
    Array.from({ length: slotCount }, (_, index) => formatDisplayValue(pick(index), point.unit));

  const columns: ExplorerColumn[] = [];
  for (const point of selected) {
    const { name, qualifier } = splitQualifier(point);
    columns.push({
      field: point.pointId,
      label: withUnit(point.name, point.unit),
      name,
      detail: detailLine(qualifier, point.unit),
      values: slotValues(point, index => {
        const slot = point.slots[index];
        return slot ? slotSeriesValue(point, slot) : null;
      }),
    });

    if (isRegister(point)) {
      columns.push({
        field: `${point.pointId}:reading`,
        label: withUnit(labels.meterReading(point.name), point.unit),
        name: labels.meterReading(name),
        detail: detailLine(qualifier, point.unit),
        values: slotValues(point, index => point.slots[index]?.value ?? null),
      });
    }

    const groupKey = sourceKey(currentSource(point));
    const group = groups.get(groupKey);
    if (group && lastSelectedPhase.get(groupKey) === point.pointId) {
      const source = currentSource(point);
      const meter = [source?.ldn, source?.label ?? source?.contextNumber]
        .filter(part => part !== null && part !== undefined)
        .join(' ');
      const sum = phaseSum(group, slotCount);
      columns.push({
        field: `phase-total:${groupKey}`,
        label: withUnit(
          groups.size > 1 ? `${meter} ${labels.phaseTotal}`.trim() : labels.phaseTotal,
          group[0].unit,
        ),
        name: labels.phaseTotal,
        detail: detailLine(groups.size > 1 && meter ? meter : null, group[0].unit),
        values: sum.map(value => formatDisplayValue(value, group[0].unit)),
      });
    }

    if (forecast?.pointId === point.pointId) {
      columns.push(
        {
          field: 'pv-forecast:weather',
          label: withUnit(labels.forecastWeather, 'Wh'),
          name: labels.forecastWeather,
          detail: detailLine(null, 'Wh'),
          values: forecast.series.weather.map(value => formatDisplayValue(value, 'Wh')),
        },
        {
          field: 'pv-forecast:clear-sky',
          label: withUnit(labels.forecastClearSky, 'Wh'),
          name: labels.forecastClearSky,
          detail: detailLine(null, 'Wh'),
          values: forecast.series.clearSky.map(value => formatDisplayValue(value, 'Wh')),
        },
      );
    }
  }
  return columns;
}
